import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';
import { FilePlus, Search, ArrowDown, ArrowUp } from "../../files/icons/index.js";

import { Status } from '../components/component.js';
import Loading from '../components/loading.js';
import Helmet from "../components/helmet.js";

import { DatetimeToDate, DateToFormat } from "../../utils/datetime.js";

function getPartnerStatusLabel(statusType) {
  const statusMap = {
    "TO_DO": "Submitted",
    "COMPLETED": "Approved",
    "CANCELED": "Denied"
  };
  return statusMap[statusType] || statusType;
}

function Referrals(props) {
  const { notificationObject } = props;

  const context = useContext(AppContext);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const templateID = searchParams.get('templateID');
  const importID = searchParams.get('importID');

  const [loading, setLoading] = useState(true);

  const step = 10;
  const [prompt, setPrompt] = useState(null);
  const [filter, setFilter] = useState({
    templateID: templateID ? [templateID] : null,
    importID: importID ? [importID] : null,
    //statusType: ["TO_DO"],
  });
  const [sorter, setSorter] = useState({ field: "createdAt", order: "desc" });

  const [limit, setLimit] = useState(step);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);

  const [referrals, setReferrals] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/partner/referrals', {
        prompt: prompt,
        filter: filter,
        sorter: sorter,
        limit: limit,
        offset: offset,
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((referrals, settings) => {
      setFilter(referrals.data.filter);
      setReferrals(referrals.data.referrals);
      setCount(referrals.data.count);

      setSettings(settings.data);

      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  useEffect(() => {
    setOffset(0); // Reset offset when sorting changes

    let u = context.addSpiner();
    axios.post('/api/partner/referrals', {
      prompt: prompt,
      filter: filter,
      sorter: sorter,
      limit: step,
      offset: 0,
    }).then((response) => {
      setFilter(response.data.filter);
      setReferrals(response.data.referrals);
      setLimit(response.data.limit);
      setOffset(response.data.offset);
      setCount(response.data.count);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, [sorter]);

  function extendLimit() {
    let u = context.addSpiner();
    axios.post('/api/partner/referrals', {
      prompt: prompt,
      filter: filter,
      sorter: sorter,
      limit: limit+step,
      offset: limit,
    }).then((response) => {
      setFilter(response.data.filter);
      setReferrals((prevReferrals) => {
        const newReferrals = response.data.referrals.filter(
          (newReferral) => !prevReferrals.some((prevReferral) => prevReferral.id === newReferral.id)
        );
        return [...prevReferrals, ...newReferrals];
      });
      setLimit(response.data.limit);
      setOffset(response.data.offset);
      setCount(response.data.count);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function search() {
    let u = context.addSpiner();
    axios.post('/api/partner/referrals', {
      prompt: prompt,
      sorter: sorter,
      limit: step,
      offset: 0,
    }).then((response) => {
      setFilter(response.data.filter);
      setReferrals(response.data.referrals);
      setLimit(response.data.limit);
      setOffset(response.data.offset);
      setCount(response.data.count);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeSort(field) {
    if (sorter.field === field) {
      // Toggle order if same field
      setSorter({
        field: field,
        order: sorter.order === "asc" ? "desc" : "asc"
      });
    } else {
      // Set new field with default order
      setSorter({
        field: field,
        order: "desc"
      });
    }
  }

  function getSortLabel(field) {
    const labels = {
      "createdAt": "Create date",
      "firstName": "Name",
      "statusType": "Status"
    };
    return labels[field] || field;
  }

  function renderReferrals() {
    var filteredReferrals = referrals;

    if (filteredReferrals.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No referrals found</div>
        </div>
      );
    }

    return filteredReferrals.map((referral) => {
      return (
        <div onClick={() => navigate(`/referral/${referral.id}`)} className="card clickable mb-2">
          <div className="card-content sm">
            <div className="row">
              <div className="col-xs-3">
                <div className="typo-strong-175">{referral.firstName + " " + referral.lastName}</div>
                <div className="typo-normal-150">{referral.partnerName}</div>
              </div>
              <div className="col-xs-3">
                <div className="typo-normal-150">{referral.message}</div>
              </div>
              <div className="col-xs-3 col-xs-offset-1">
              </div>
              <div className="col-xs-2 text-end">
                {referral.statusType === "TO_DO" && (
                  <Status color={'grey'} muted={true} text={getPartnerStatusLabel(referral.statusType)} />
                )}
                {referral.statusType === "COMPLETED" && (
                  <Status color={'green'} muted={true} text={getPartnerStatusLabel(referral.statusType)} />
                )}
                {referral.statusType === "CANCELED" && (
                  <Status color={'red'} muted={true} text={getPartnerStatusLabel(referral.statusType)} />
                )}
                <div className="typo-normal-125 mt-2">{DateToFormat(DatetimeToDate(referral.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(referral.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Referrals" />

      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center">
                <span className="typo-hero-small">Referrals</span>
              </div>
              <div className="w-25 text-end">
                <div className="item typo-normal-150 ms-2" onClick={() => navigate('/referral/create')}><FilePlus height={16} width={16} color={'grey'} /> Create Referral</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            
            <div className="card shadowmore mb-4">
              <div className="card-content sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex justify-content-start align-items-center w-75">
                    <Search height={16} width={16} color={'grey'} />
                    <input type="text" className="input borderless w-100 ms-2" placeholder="Type and press enter to search..." value={prompt || ''} onChange={(e) => setPrompt(e.target.value)} onKeyUp={(e) => e.key === 'Enter' && search()} autoFocus={true} />
                  </div>
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="typo-normal-125 me-2">Sort by:&nbsp;</div>
                    <div className="item gap-no typo-normal-125 clickable" onClick={() => changeSort("createdAt")}>
                      {getSortLabel("createdAt")} {sorter.field === "createdAt" && (sorter.order === "asc" ? <ArrowUp height={12} width={12} color={'grey'} /> : <ArrowDown height={12} width={12} color={'grey'} />)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {renderReferrals()}

            {(count !== 0 && limit < count) && (
              <div className="d-flex justify-content-center align-items-center">
                <div className="item typo-normal-150" onClick={() => extendLimit()}>show more</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Referrals;

